import { DateTime } from 'luxon'
import {
  ADM_TRAINING_PROMOTION_TOOLTIP,
  ADM_TRAINING_VISIBILITY_TOOLTIP,
  getVisibilityDisabledTooltip,
} from 'lib/coach-profile/promotion-tooltips'

export {
  ADM_TRAINING_VISIBILITY_TOOLTIP,
  ADM_TRAINING_PROMOTION_TOOLTIP,
  getVisibilityDisabledTooltip,
}

export const hasVisibleFacility = (facilities = []) =>
  facilities.some(facility =>
    facility.coachProfilesFacilities?.some(cpf => cpf.visible),
  )

export const getFacilityLocation = (coachProfile, facilities = []) => {
  const profileFacility = coachProfile?.facilities?.[0]
  const facilityName =
    profileFacility?.facilityName ||
    facilities.find(facility => facility.name)?.name ||
    null
  const city = profileFacility?.city || coachProfile?.city
  const state = profileFacility?.state || coachProfile?.state

  if (facilityName && city && state) {
    return `${facilityName} • ${city}, ${state}`
  }

  if (city && state) {
    return `${city}, ${state}`
  }

  return facilityName
}

export const countInDateRange = (
  items,
  startDate,
  endDate,
  dateField = 'createdAt',
) => {
  if (!items?.length) {
    return 0
  }

  const rangeStart = startDate.startOf('day')
  const rangeEnd = endDate.endOf('day')

  return items.filter(item => {
    const date = DateTime.fromISO(item[dateField])
    return date.isValid && date >= rangeStart && date <= rangeEnd
  }).length
}

export const getPercentChange = (current, previous) => {
  const currentValue = Number(current) || 0
  const previousValue = Number(previous) || 0

  if (previousValue === 0) {
    return currentValue === 0 ? 0 : null
  }

  return Math.round(((currentValue - previousValue) / previousValue) * 100)
}

export const getStatusBanner = ({
  coachProfile,
  facilities,
  classes,
  InfoIcon,
  ErrorIcon,
  WarningIcon,
  Link,
  RouterLink,
  ADMTrainingExternalLink,
}) => {
  const { coach } = coachProfile
  const visibleFacility = hasVisibleFacility(facilities)

  if (coach.canPromoteContent && coachProfile.status === 'ACTIVE') {
    if (visibleFacility) {
      return null
    }

    return {
      text: (
        <>
          {
            'Your coach profile is currently not promoted on PGA.com. You must have at least one visible facility to be promoted. '
          }
          <Link
            to="/pga-coach/profile/edit"
            component={RouterLink}
            className={classes.bannerLink}
          >
            Click here
          </Link>
          {' to update'}
        </>
      ),
      className: classes.bannerError,
      icon: (
        <ErrorIcon className={classes.bannerIconError} fontSize="inherit" />
      ),
    }
  }

  if (coach.canPromoteContent && coachProfile.status !== 'ACTIVE') {
    return {
      text:
        'Your coach profile is currently not promoted on PGA.com. Turn on your profile visibility to begin receiving leads from prospective customers.',
      className: classes.bannerWarning,
      icon: (
        <WarningIcon className={classes.bannerIconWarning} fontSize="inherit" />
      ),
    }
  }

  return {
    text: (
      <>
        {
          'You are currently ineligible to promote your coach profile on PGA.com. Please complete '
        }
        <ADMTrainingExternalLink icon={false} />
        {' to become eligible.'}
      </>
    ),
    className: classes.bannerError,
    icon: <ErrorIcon className={classes.bannerIconError} fontSize="inherit" />,
  }
}

export const getVisibilityCaption = ({ coachProfile, facilities }) => {
  const { coach } = coachProfile
  const visibleFacility = hasVisibleFacility(facilities)

  if (!coach.canPromoteContent) {
    return ADM_TRAINING_VISIBILITY_TOOLTIP
  }

  if (coachProfile.status === 'ACTIVE') {
    if (visibleFacility) {
      return 'Your profile is currently visible on PGA.com and eligible for lead promotion.'
    }

    return 'Your profile is visible on PGA.com. Add a visible facility to become eligible for lead promotion.'
  }

  return 'Turn on profile visibility to start receiving leads from prospective customers.'
}

export const getPromotionStatus = ({ coachProfile, facilities }) => {
  const { coach } = coachProfile
  const visibleFacility = hasVisibleFacility(facilities)

  if (
    coach.canPromoteContent &&
    coachProfile.status === 'ACTIVE' &&
    visibleFacility
  ) {
    return {
      label: 'Promoted',
      tooltip:
        'Your coach profile is visible on PGA.com and eligible for lead promotion.',
      styleKey: 'promotionStatusPromoted',
    }
  }

  if (!coach.canPromoteContent) {
    return {
      label: 'Not promoted',
      tooltip: ADM_TRAINING_PROMOTION_TOOLTIP,
      styleKey: 'promotionStatusInactive',
    }
  }

  if (coachProfile.status !== 'ACTIVE') {
    return {
      label: 'Not promoted',
      tooltip:
        'Turn on public profile visibility to begin receiving leads on PGA.com.',
      styleKey: 'promotionStatusWarning',
    }
  }

  return {
    label: 'Not promoted',
    tooltip:
      'Add at least one visible facility on your profile to be promoted on PGA.com.',
    styleKey: 'promotionStatusWarning',
  }
}
